import { SparklineCard as SparklineCardBase } from "@bloomberg/media-ui-markets";
import React, { useCallback, useEffect, useMemo } from "react";
import { createClasses } from "@bloomberg/media-ui-core";
import { trackClickEvent } from "@foundation/nextjs-gtm";
import style from "./styles.module.css";
import type { TodayInMarketsItem } from "~types/types";
import { useTodayInMarketsSparklineData } from "~hooks/SectionFront/useTodayInMarketsSparklineData";
import { useTodayInMarkets } from "~context/TodayInMarketsContext";
import { TodayInMarketsSparklinePlaceholder } from "~components/TodayInMarkets/TodayInMarketsSparklineCards/TodayInMarketsSparklinePlaceholder";
import { PlaceholderWithError } from "~components/PlaceholderWithError";
import { addPageAction, logError } from "~utils/newrelic/newrelicBrowser";
import useLink from "~hooks/useLink";
import { useTranslateMessage } from "~utils/i18n";

const SparklineCard = React.memo(SparklineCardBase);

export default function TodayInMarketsSparklineCards({
  enableFocusEvents = false,
}) {
  const {
    activeTicker,
    selectedCategory,
    updateActiveTicker,
    updateCategoryWithSparklineData,
  } = useTodayInMarkets();

  const translate = useTranslateMessage();

  const link = useLink();

  const sparklineItems = useMemo(
    () => selectedCategory.dataItems.map((item: TodayInMarketsItem) => item),
    [selectedCategory.dataItems]
  );

  const sparklineTickers = useMemo(
    () => sparklineItems.map((item: TodayInMarketsItem) => item.id),
    [sparklineItems]
  );

  const {
    status,
    refetch,
    data: sparklineData = [],
  } = useTodayInMarketsSparklineData(sparklineTickers);

  useEffect(() => {
    updateCategoryWithSparklineData(selectedCategory.id, sparklineData);
  }, [sparklineData, selectedCategory.id, updateCategoryWithSparklineData]);

  const onClick = useCallback((item: TodayInMarketsItem) => {
    trackClickEvent("Market_Data", "tile_click_tim", "click", "Market Data");

    addPageAction("todayInMarketsSparklineClick", {
      ticker: item.id,
    });

    // open quotes page on click
    link.open(`/quote/${item.id}`, "_blank");
  }, []);

  const handleMouseMove = useCallback(
    (item: TodayInMarketsItem) => {
      // trigger card blur event
      updateActiveTicker(item.id);
    },
    [updateActiveTicker]
  );

  const handleMouseLeave = useCallback(() => {
    // trigger card blur event
    updateActiveTicker(undefined);
  }, [updateActiveTicker]);

  if (status === "pending") {
    return <TodayInMarketsSparklinePlaceholder />;
  }

  if (status === "error") {
    logError(new Error("TodayInMarkets Sparkline Fetch Error"), {
      selectedCategory: selectedCategory?.id,
      sparklineTickers: sparklineTickers.join(","),
    });

    return (
      <PlaceholderWithError
        placeholder={
          <div className={style.todayInMarketsErrorWrapper}>
            <TodayInMarketsSparklinePlaceholder />
          </div>
        }
        errorMessage={translate({
          id: "errMarketData",
          defaultMessage: "There was an error loading market data.",
        })}
        onRetryClick={() => refetch({ throwOnError: true })}
      />
    );
  }

  return (
    <>
      {sparklineItems.map((item, index) => {
        const containerClass = createClasses([
          style.todayInMarketsSparklineCardContainer,
          {
            [style.active]: enableFocusEvents && item.id === activeTicker,
          },
        ]);

        const focusEventHandlers = {
          onMouseMove: () => handleMouseMove(item),
          onMouseLeave: () => handleMouseLeave(),
        };

        return (
          <div
            key={index}
            className={containerClass}
            onClick={() => onClick(item)}
            {...(enableFocusEvents && focusEventHandlers)}
          >
            <SparklineCard
              className={style.todayInMarketsSparklineCard}
              currency={item.currency}
              percentChange={item.percentChange}
              price={item.price}
              priceSet={item.priceSet}
              securityName={item.id}
              yesterdayPrice={item.yesterdayPrice}
              directionBy="percentChange"
              useGradualFade
            />
          </div>
        );
      })}
    </>
  );
}
